import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SymptomAnalyzer from "@/components/SymptomAnalyzer";
import AnalysisResults from "@/components/AnalysisResults";
import { AnalysisResult, SymptomAnalysisInput } from "@/types/naturopathy";
import { naturopathyService } from "@/services/naturopathyService";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Sprout } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";

const NaturopathyApp: React.FC = () => {
  const navigate = useNavigate();
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [symptomsInput, setSymptomsInput] = useState<string>("");
  const [patientAge, setPatientAge] = useState<number | undefined>(undefined);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const getUserId = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getUserId();
  }, []);

  const handleAnalyze = async (input: SymptomAnalysisInput) => {
    setIsAnalyzing(true);
    setSymptomsInput(input.symptoms);
    setPatientAge(input.age);
    try {
      const result = await naturopathyService.analyzeSymptoms(input, userId || undefined);
      setAnalysisResult(result);

      if (result.triage.level === "urgent_medical") {
        toast({
          title: "Consultation médicale urgente",
          description: "Les symptômes décrits nécessitent une évaluation médicale immédiate.",
          variant: "destructive",
        });
      } else if (result.interactions_alert.length > 0) {
        toast({
          title: "Interactions détectées",
          description: `${result.interactions_alert.length} interaction(s) médicamenteuse(s) identifiée(s).`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Analyse terminée",
          description: `${result.recommendations.length} recommandation(s) générée(s) avec succès.`,
        });
      }
    } catch (error) {
      console.error("Erreur lors de l'analyse:", error);
      toast({
        title: "Erreur d'analyse",
        description: "Une erreur est survenue lors de l'analyse. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleExportPDF = () => {
    // Simulation d'export PDF
    toast({
      title: "Export en cours",
      description: "La fiche de recommandations sera bientôt téléchargée.",
    });

    // Ici on implémenterait la génération du PDF
    console.log("Export PDF:", analysisResult);
  };

  const handleReset = () => {
    setAnalysisResult(null);
    setPatientAge(undefined);
    toast({
      title: "Nouvelle analyse",
      description: "Vous pouvez maintenant saisir de nouveaux symptômes.",
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Commencer l'analyse"
        subtitle="Assistant intelligent pour l'analyse naturopathique et la génération de recommandations personnalisées"
        icon={<Sprout className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
      />

      <div className="space-y-8">

        {/* Contenu principal */}
        {!analysisResult ? (
          <SymptomAnalyzer onAnalyze={handleAnalyze} isLoading={isAnalyzing} />
        ) : (
          <AnalysisResults
            result={analysisResult}
            symptomsInput={symptomsInput}
            patientAge={patientAge}
            onExportPDF={handleExportPDF}
            onReset={handleReset}
          />
        )}

        {/* Footer avec informations légales */}
        <footer className="text-center text-sm text-muted-foreground mt-12 pt-8 border-t border-border">
          <p className="mb-2">
            <strong>REGARD DE NATURE - Naturopathie & Iridologie</strong> - Assistant naturopathique intelligent
          </p>
          <p className="max-w-4xl mx-auto">
            Cette application fournit des conseils naturopathiques à titre informatif uniquement. Elle ne remplace pas
            un diagnostic médical professionnel. En cas de symptômes graves ou persistants, consultez immédiatement un
            professionnel de santé qualifié.
          </p>
          <p className="mt-4 text-xs">Développé avec ❤️ pour les naturopathes et leurs consultants</p>
        </footer>
      </div>
    </PageContainer>
  );
};

export default NaturopathyApp;
